/**
 * A "New …" button that actually creates something.
 *
 * WHY THIS EXISTS
 *
 * 82 of the SPA's 120 feature pages rendered action buttons with no onClick at
 * all — `<Button variant="primary">New production order</Button>` and nothing
 * else. Clicking did nothing, silently: no dialog, no error, no console
 * message. The product looked complete and was read-only, and the only way to
 * get a record into it was the API.
 *
 * Writing 82 bespoke forms would be 82 chances to diverge on validation,
 * error handling and what happens after a save. This is one control: a button,
 * a modal, a field spec, and a POST. A page spends about ten lines on it.
 *
 * WHAT IT DELIBERATELY DOES
 *
 * Surfaces the API's own error text. The API already returns precise messages
 * — "supplierNo, name and currencyCode are required", "Unknown timezone
 * 'Mars/Olympus'" — and a form that replaces those with "Something went wrong"
 * throws away the most useful thing it has. That text is English written for
 * developers, so in another language the sentence comes from the error
 * catalog and the API's text stays one click away under Details (ErrorText).
 *
 * Sends an Idempotency-Key where the endpoint demands one, so a double-click
 * on a slow connection cannot raise two invoices.
 */
import { useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input, Select } from "./ui";
import { ErrorText } from "./ErrorText";
import { post } from "@/lib/api/hooks";
import { tenantKey } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";

export interface FormField {
  name: string;
  label: string;
  type?: "text" | "number" | "date" | "select" | "textarea";
  required?: boolean;
  placeholder?: string;
  hint?: string;
  /** For select fields. */
  options?: Array<{ value: string; label: string }>;
  /** Prefilled when the form opens. */
  defaultValue?: string;
}

interface Props {
  /** Button text, e.g. "New product". */
  label: string;
  /** API path to POST to, e.g. "/products". */
  path: string;
  /** The fields to collect. */
  fields: FormField[];
  /** Query key to refresh after a successful create. */
  invalidate: string[];
  /** Endpoints marked @Idempotent refuse a request without a key. */
  idempotent?: boolean;
  /** Shape the request body from the collected values. */
  transform?: (values: Record<string, string>) => Record<string, unknown>;
  /** Called with the created record. */
  onCreated?: (record: unknown) => void;
  children?: ReactNode;
}

export function CreateRecordButton({
  label,
  path,
  fields,
  invalidate,
  idempotent,
  transform,
  onCreated,
}: Props) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  // The error itself, not its text: the words are chosen when it is shown,
  // in the language of the moment.
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  // One Idempotency-Key per dialog, made when it opens and reused until a
  // save succeeds (NUM-P0 §3.4): a second Save after a timeout repeats the
  // same request, which the server answers with the record it already made,
  // instead of sending a new key and creating a second one.
  const idempotencyKey = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  function openForm() {
    const initial: Record<string, string> = {};
    for (const f of fields)
      if (f.defaultValue) initial[f.name] = f.defaultValue;
    setValues(initial);
    setError(null);
    idempotencyKey.current = idempotent ? crypto.randomUUID() : null;
    setOpen(true);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Only send what was filled in. Sending "" for an untouched optional
      // field is how a uuid column ends up with an empty string.
      const filled: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== undefined && v !== "") filled[k] = v;
      }
      const body = transform ? transform(filled) : filled;
      // The dialog's key, made when it opened (openForm).
      const created = await post(
        path,
        body,
        idempotencyKey.current ?? undefined,
      );
      await queryClient.invalidateQueries({ queryKey: tenantKey(invalidate) });
      idempotencyKey.current = null;
      setOpen(false);
      onCreated?.(created);
    } catch (err) {
      // The API's message, not a generic one. It names the missing field.
      setError(err ?? new Error("The record could not be created."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button variant="primary" onClick={openForm}>
        {label}
      </Button>

      {open && (
        <div
          className="atlas-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onClick={(e) => {
            // Only a click on the backdrop itself closes; a click inside the
            // panel must not discard what someone has typed.
            if (e.target === e.currentTarget && !saving) setOpen(false);
          }}
        >
          <div className="atlas-modal">
            <form onSubmit={submit}>
              <h2 style={{ margin: 0, marginBlockEnd: 4, fontSize: 18 }}>
                {label}
              </h2>
              <p
                className="atlas-muted"
                style={{ fontSize: 13, marginBlockStart: 0 }}
              >
                {t("ui.requiredNote")}
              </p>

              {fields.map((f) => (
                <Field
                  key={f.name}
                  label={f.label}
                  required={f.required}
                  hint={f.hint}
                >
                  {f.type === "select" ? (
                    <Select
                      value={values[f.name] ?? ""}
                      required={f.required}
                      onChange={(e) =>
                        setValues((v) => ({ ...v, [f.name]: e.target.value }))
                      }
                    >
                      <option value="">{t("common.select")}</option>
                      {(f.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : (f.type ?? "text")}
                      step={f.type === "number" ? "any" : undefined}
                      value={values[f.name] ?? ""}
                      required={f.required}
                      placeholder={f.placeholder}
                      onChange={(e) =>
                        setValues((v) => ({ ...v, [f.name]: e.target.value }))
                      }
                    />
                  )}
                </Field>
              ))}

              {error !== null && (
                <div
                  role="alert"
                  style={{
                    color: "var(--atlas-danger)",
                    fontSize: 13,
                    marginBlock: 8,
                  }}
                >
                  <ErrorText error={error} />
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginBlockStart: 16,
                }}
              >
                <Button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? t("common.saving") : t("common.create")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
